/**
 * EDITOR-6558: Allowlist of `MenuItem.value.name` strings that the main
 * editor toolbar should expose when the editor is in Markdown Mode.
 *
 * The strings here come from `editor-plugin-insert-block`'s
 * `ui/ToolbarInsertBlock/item.tsx`, where each item factory hard-codes a
 * `name`. Anything not on this allowlist is hidden from the toolbar
 * (button row + overflow dropdown) while Markdown Mode is active.
 *
 * The list is the toolbar-flavoured equivalent of
 * `MARKDOWN_COMPATIBLE_QUICK_INSERT_ITEM_IDS`. Entries are intentionally
 * kept minimal — only nodes/marks that survive a clean GFM round-trip via
 * `@atlassian/mdast-util-from-pm` are included.
 *
 * If you add a new mdast handler that lets a previously-excluded node
 * round-trip cleanly, add the matching name here so the toolbar surfaces it.
 */
export const MARKDOWN_COMPATIBLE_TOOLBAR_ITEM_NAMES: readonly string[] = [
	// Inline marks / nodes with a clean GFM equivalent:
	'link', // [text](url)
	'image upload', // ![alt](src) — image *upload* button only; rich `media` excluded
	'codeblock', // ``` fenced code block
	'blockquote', // > blockquote
	'horizontalrule', // ---
	'table', // | a | b |
	'table selector', // table size picker (same target node)
	'action', // - [ ] task list (GFM task lists)
];

export const MARKDOWN_COMPATIBLE_TOOLBAR_ITEM_NAMES_SET: ReadonlySet<string> = new Set(
	MARKDOWN_COMPATIBLE_TOOLBAR_ITEM_NAMES,
);

/**
 * Predicate suitable for passing as `InsertBlockPluginOptions.itemFilter`
 * when in Markdown Mode. Returns `true` for items whose `value.name` is on
 * the allowlist.
 *
 * Items without a `value.name` are conservatively allowed — the
 * `insertMenuItems` extension surface lets consumers inject custom items
 * and we don't want to silently drop those without an explicit signal.
 */
export const isMarkdownCompatibleToolbarItem = (item: { value: { name: string } }): boolean => {
	if (!item.value?.name) {
		return true;
	}
	return MARKDOWN_COMPATIBLE_TOOLBAR_ITEM_NAMES_SET.has(item.value.name);
};
